import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Loader2, RefreshCw, ShieldAlert } from 'lucide-react';
import { API } from '../../utils/api';

export function AdminDevConfig({ headers }) {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDev, setIsDev] = useState(false);
  const [phones, setPhones] = useState('');
  const [devFrontendUrl, setDevFrontendUrl] = useState('');
  const [appUrl, setAppUrl] = useState('');

  const hydrate = (data) => {
    setSnap(data);
    setIsDev(data.app_env === 'development' || data.is_dev);
    setPhones((data.otp_bypass_phones || []).join('\n'));
    setDevFrontendUrl(data.dev_frontend_url || '');
    setAppUrl(data.app_url || '');
  };

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/dev-config`, { headers });
      hydrate(res.data);
    } catch (e) {
      toast.error('Failed to load dev config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfig(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        app_env: isDev ? 'development' : 'production',
        otp_bypass_phones: phones.split(/[\n,]/).map((p) => p.trim()).filter(Boolean),
        dev_frontend_url: devFrontendUrl.trim(),
        app_url: appUrl.trim(),
      };
      const res = await axios.put(`${API}/admin/dev-config`, payload, { headers });
      hydrate(res.data);
      toast.success('Dev config saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save dev config');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-muted-foreground py-10" data-testid="dev-config-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading dev config…</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-dev-config">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2"><ShieldAlert className="h-6 w-6 text-amber-600" /> Dev Config</h2>
          <p className="text-sm text-muted-foreground">Runtime development overrides. Changes apply immediately (no restart).</p>
        </div>
        <Button variant="outline" onClick={fetchConfig} data-testid="dev-config-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Environment
            <Badge variant={isDev ? 'destructive' : 'outline'} data-testid="dev-config-env-badge">
              {isDev ? 'DEVELOPMENT' : 'PRODUCTION'}
            </Badge>
          </CardTitle>
          <CardDescription>All dev-only overrides below are ignored unless the environment is set to Development.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-3">
            <Switch checked={isDev} onCheckedChange={setIsDev} data-testid="dev-config-env-switch" />
            <Label>Development mode {isDev ? '(overrides active)' : '(overrides inactive)'}</Label>
          </div>

          <div>
            <Label>OTP bypass phone numbers</Label>
            <p className="text-xs text-muted-foreground mb-1">One E.164 number per line (or comma-separated). These skip real SMS/OTP in dev.</p>
            <Textarea
              rows={3}
              value={phones}
              onChange={(e) => setPhones(e.target.value)}
              placeholder={'+46701234567\n+46709876543'}
              data-testid="dev-config-otp-phones"
            />
          </div>

          <div>
            <Label>Dev frontend URL</Label>
            <p className="text-xs text-muted-foreground mb-1">Where the browser returns after Swish + base for QR / /pay links while in dev.</p>
            <Input
              value={devFrontendUrl}
              onChange={(e) => setDevFrontendUrl(e.target.value)}
              placeholder="https://your-dev-frontend.example.com"
              data-testid="dev-config-dev-frontend-url"
            />
          </div>

          <div>
            <Label>Production app URL</Label>
            <p className="text-xs text-muted-foreground mb-1">Public base URL used for QR / /pay links in production.</p>
            <Input
              value={appUrl}
              onChange={(e) => setAppUrl(e.target.value)}
              placeholder="https://paymig.app"
              data-testid="dev-config-app-url"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={save} disabled={saving} data-testid="dev-config-save">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Currently active (resolved)</CardTitle>
          <CardDescription>What the backend is using right now, after applying overrides.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm" data-testid="dev-config-resolved">
          <div><span className="text-muted-foreground">Is dev:</span> <strong>{String(snap?.is_dev)}</strong></div>
          <div><span className="text-muted-foreground">Callback base URL:</span> <strong className="break-all">{snap?.callback_base_url || '—'}</strong></div>
          <div><span className="text-muted-foreground">Public app URL (QR / pay):</span> <strong className="break-all">{snap?.public_app_url || '—'}</strong></div>
          <div><span className="text-muted-foreground">Active OTP bypass numbers:</span> <strong>{(snap?.otp_bypass_phones || []).join(', ') || '—'}</strong></div>
          <div className="pt-2 border-t mt-2 text-xs text-muted-foreground">
            Env defaults — env: {snap?.env_defaults?.app_env}; frontend: {snap?.env_defaults?.dev_frontend_url || '—'}; app: {snap?.env_defaults?.app_url || '—'}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
